import React from 'react';
import { Box, Card, CardContent, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';

type Campaign = any;

interface CampaignInformationProps {
  campaign: Campaign;
  formatUtcToLocal: (date: string | Date) => string;
  getStatusChip: (status: string) => React.ReactElement;
  dataSetName?: string | null;
  /** Friendly template label (from content template); link opens Templates list */
  templateDisplayName?: string | null;
}

export const CampaignInformation: React.FC<CampaignInformationProps> = ({
  campaign,
  formatUtcToLocal,
  getStatusChip,
  dataSetName,
  templateDisplayName,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: theme.palette.heading?.main ?? theme.palette.secondary.main }}>
            Campaign Information
          </Typography>
        </Box>
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon sx={{ mr: 1, fontSize: 20 }} />
                    Time Information
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: theme.palette.secondary.main }}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box display="flex" alignItems="center">
                    <AccessTimeIcon sx={{ mr: 1, fontSize: 16 }} />
                    Start Time
                  </Box>
                </TableCell>
                <TableCell>{formatUtcToLocal(campaign.startTimestamp)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box display="flex" alignItems="center">
                    <TimerIcon sx={{ mr: 1, fontSize: 16 }} />
                    Time Interval
                  </Box>
                </TableCell>
                <TableCell>{campaign.timeInterval} seconds</TableCell>
              </TableRow>
              {campaign.completedOn && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Box display="flex" alignItems="center">
                      <CheckCircleOutlineIcon sx={{ mr: 1, fontSize: 16 }} />
                      Finish Time
                    </Box>
                  </TableCell>
                  <TableCell>{formatUtcToLocal(campaign.completedOn)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box display="flex" alignItems="center">
                    <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
                    Created On
                  </Box>
                </TableCell>
                <TableCell>{formatUtcToLocal(campaign.createdOn)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>
                  <Box display="flex" alignItems="center">
                    <InfoOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
                    Status
                  </Box>
                </TableCell>
                <TableCell>
                  {getStatusChip(campaign.status)}
                </TableCell>
              </TableRow>
              {templateDisplayName && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Box display="flex" alignItems="center">
                      <ArticleOutlinedIcon sx={{ mr: 1, fontSize: 16 }} />
                      Template
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      underline="hover"
                      color="secondary"
                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => navigate('/instant-updates/templates')}
                    >
                      {templateDisplayName}
                    </Link>
                  </TableCell>
                </TableRow>
              )}
              {dataSetName && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    <Box display="flex" alignItems="center">
                      <StorageIcon sx={{ mr: 1, fontSize: 16 }} />
                      Data Set
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      underline="hover"
                      color="secondary"
                      sx={{ cursor: 'pointer', fontWeight: 500 }}
                      onClick={() => navigate(`/instant-updates/data-sets/${encodeURIComponent(dataSetName)}`)}
                    >
                      {dataSetName}
                    </Link>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

export default CampaignInformation;


